/**
 * 週間ビューページ
 *
 * 1週間分の習慣達成状況をテーブル形式で表示する。
 * 各習慣について、曜日ごとのチェック状態と達成率を確認できる。
 *
 * 構成:
 * - ヘッダー行: 曜日ラベルと日付
 * - 習慣行: 習慣名、曜日ごとのチェックボックス、達成率
 *
 * Rails で言えば:
 * - app/controllers/habits_controller.rb#weekly のビューに相当
 * - データは Inertia.js 経由で props として受け取る
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import { Head, router } from '@inertiajs/react'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  HabitLayout,
  HabitCheckbox,
  HabitForm,
  HabitLogModal,
} from '@/components/habits'
import { SatisfactionCycler } from '@/components/satisfaction'
import { useHabitToggle, useHabitSchedule, useAchievementRate } from '@/hooks'
import { cn } from '@/lib/utils'
import { formatDateLocal, getDaysInRange, parseDateLocal } from '@/lib/date'
import type { HabitsWeeklyProps } from '@/types'
import { WEEKDAY_LABELS } from '@/types'
import * as Routes from '@/lib/routes'

// === ヘルパー関数 ===

/**
 * 週ラベルを生成（例: "1月6日 - 1月12日"）
 * 年またぎの場合は年も表示（例: "2024年12月30日 - 2025年1月5日"）
 */
function formatWeekLabel(startDate: string, endDate: string): string {
  const start = parseDateLocal(startDate)
  const end = parseDateLocal(endDate)

  const startYear = start.getFullYear()
  const endYear = end.getFullYear()

  if (startYear !== endYear) {
    // 年またぎの場合
    return `${startYear}年${start.getMonth() + 1}月${start.getDate()}日 - ${endYear}年${end.getMonth() + 1}月${end.getDate()}日`
  }

  // 同じ年の場合
  return `${start.getMonth() + 1}月${start.getDate()}日 - ${end.getMonth() + 1}月${end.getDate()}日`
}

export default function HabitsWeekly({
  habits: initialHabits,
  startDate,
  endDate,
  today, // サーバーから提供される「今日」を使用
  satisfactions = {},
}: HabitsWeeklyProps) {
  // === 状態管理 ===
  const [habits, setHabits] = useState(initialHabits)
  const [showForm, setShowForm] = useState(false)

  // props の変更を監視してローカル状態を同期
  useEffect(() => {
    setHabits(initialHabits)
  }, [initialHabits])

  // === カスタムフック ===
  // 日付範囲を計算
  const days = useMemo(
    () => getDaysInRange(startDate, endDate),
    [startDate, endDate]
  )

  // 習慣の完了/予定判定フック
  const { isCompleted, isScheduled } = useHabitSchedule()

  // 達成率計算フック
  const { calculateRate } = useAchievementRate(days)

  // トグル処理フック
  const {
    toggle,
    isPending,
    logModalTarget,
    closeLogModal,
    saveLog,
    uncomplete,
  } = useHabitToggle({
    onError: (error) => console.error('Toggle failed:', error),
  })

  // === イベントハンドラー ===
  /**
   * 習慣の完了/未完了を切り替える
   *
   * 楽観的更新パターン:
   * 1. UI を即座に更新（completedDates と achievementRate を更新）
   * 2. サーバーにリクエストを送信
   * 3. エラー時は toggle フック内で元に戻す
   */
  const handleToggle = useCallback(
    (habitId: number, date: Date) => {
      const habit = habits.find((h) => h.id === habitId)
      if (!habit) return

      const dateStr = formatDateLocal(date)
      const completed = isCompleted(habit, date)

      toggle(
        habitId,
        dateStr,
        completed,
        (newCompleted) => {
          setHabits((prev) =>
            prev.map((h) => {
              if (h.id !== habitId) return h

              // 完了日リストを更新
              const newDates = newCompleted
                ? [...(h.completedDates || []), dateStr]
                : (h.completedDates || []).filter((d) => d !== dateStr)

              // 達成率を再計算
              const newRate = calculateRate(h, newDates)

              return {
                ...h,
                completedDates: newDates,
                achievementRate: newRate,
              }
            })
          )
        },
        {
          enabled: !!habit.loggingEnabled,
          habitName: habit.name,
          existingLog: habit.logDetails?.[dateStr],
        }
      )
    },
    [habits, isCompleted, toggle, calculateRate]
  )

  /**
   * 前週/次週に移動
   */
  const navigateWeek = (direction: 'prev' | 'next') => {
    const start = parseDateLocal(startDate)
    const newStart = new Date(start)
    newStart.setDate(start.getDate() + (direction === 'next' ? 7 : -7))
    const weekParam = formatDateLocal(newStart)
    router.get(
      Routes.weekly_habits_path(),
      { week: weekParam },
      { preserveState: true }
    )
  }

  // === レンダリング ===
  return (
    <>
      <Head title="週間ビュー | Habit Tracker" />

      <HabitLayout
        title="週間ビュー"
        action={
          <Button onClick={() => setShowForm(true)} className="touch-target">
            <Plus className="mr-1 h-5 w-5" />
            <span className="hidden sm:inline">新しい習慣</span>
          </Button>
        }
      >
        {/* === 週ナビゲーション === */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateWeek('prev')}
            className="touch-target"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-lg font-semibold">
            {formatWeekLabel(startDate, endDate)}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateWeek('next')}
            className="touch-target"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* === ヘッダー行 === */}
        <div className="border-border mb-2 flex items-end rounded-lg border border-transparent p-2 sm:p-3">
          {/* 習慣名カラム */}
          <div
            className="text-muted-foreground w-[25%] text-xs font-medium"
            style={{ borderLeftWidth: '3px', borderLeftColor: 'transparent' }}
          >
            習慣
          </div>

          {/* 曜日ラベル */}
          <div className="flex w-[55%] justify-around sm:w-[60%]">
            {days.map((day) => (
              <div key={formatDateLocal(day)} className="text-center">
                <div className="text-muted-foreground text-[10px]">
                  {WEEKDAY_LABELS[day.getDay()]}
                </div>
                <div
                  className={cn(
                    'text-foreground text-xs',
                    // 今日の日付をハイライト
                    formatDateLocal(day) === today && 'text-primary font-bold'
                  )}
                >
                  {day.getDate()}
                </div>
              </div>
            ))}
          </div>

          {/* 達成率カラム */}
          <div className="text-muted-foreground w-[20%] text-right text-xs font-medium sm:w-[15%]">
            達成率
          </div>
        </div>

        {/* === 満足度行 === */}
        <div className="border-border mb-2 flex items-center rounded-lg border border-dashed p-2 sm:p-3">
          <div
            className="text-muted-foreground w-[25%] text-xs font-medium"
            style={{ borderLeftWidth: '3px', borderLeftColor: 'transparent' }}
          >
            気分
          </div>
          <div className="flex w-[55%] justify-around sm:w-[60%]">
            {days.map((day) => {
              const dateStr = formatDateLocal(day)
              return (
                <div key={`sat-${dateStr}`} className="flex justify-center">
                  <SatisfactionCycler
                    level={satisfactions[dateStr] ?? null}
                    date={dateStr}
                    size="sm"
                  />
                </div>
              )
            })}
          </div>
          <div className="w-[20%] sm:w-[15%]" />
        </div>

        {/* === 習慣行 === */}
        <div className="space-y-2">
          {habits.map((habit) => (
            <div
              key={habit.id}
              className="border-border flex items-center rounded-lg border p-2 sm:p-3"
              style={{
                borderLeftWidth: '3px',
                borderLeftColor: habit.color || '#3b82f6',
              }}
            >
              {/* 習慣名 */}
              <div className="text-foreground w-[25%] min-w-0 truncate text-sm font-medium">
                {habit.name}
              </div>

              {/* 曜日ごとのチェックボックス */}
              <div className="flex w-[55%] justify-around sm:w-[60%]">
                {days.map((day) => {
                  const dateStr = formatDateLocal(day)
                  const scheduled = isScheduled(habit, day)
                  const completed = isCompleted(habit, day)
                  // extra は !scheduled で判定（isExtraCompletion は未完了時には false になるため）

                  return (
                    <div key={dateStr} className="flex justify-center">
                      {/* モバイル: xs (16px) */}
                      <HabitCheckbox
                        size="xs"
                        checked={completed}
                        pending={isPending(habit.id, dateStr)}
                        onClick={() => handleToggle(habit.id, day)}
                        color={habit.color ?? undefined}
                        extra={!scheduled}
                        className="sm:hidden"
                      />
                      {/* デスクトップ: sm (32px) - タイムラインと同等 */}
                      <HabitCheckbox
                        size="sm"
                        checked={completed}
                        pending={isPending(habit.id, dateStr)}
                        onClick={() => handleToggle(habit.id, day)}
                        color={habit.color ?? undefined}
                        extra={!scheduled}
                        className="hidden sm:flex"
                      />
                    </div>
                  )
                })}
              </div>

              {/* 達成率（色分け表示） */}
              <div className="w-[20%] text-right sm:w-[15%]">
                <span
                  className={cn(
                    'text-xs font-medium',
                    (habit.achievementRate ?? 0) >= 100
                      ? 'text-green-600' // 100%以上: 緑
                      : (habit.achievementRate ?? 0) >= 80
                        ? 'text-green-600' // 80%以上: 緑
                        : (habit.achievementRate ?? 0) >= 50
                          ? 'text-yellow-600' // 50%以上: 黄
                          : 'text-muted-foreground' // 50%未満: グレー
                  )}
                >
                  {habit.achievementRate ?? 0}%
                  {(habit.achievementRate ?? 0) > 100 && '★'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* 習慣がない場合の空状態 */}
        {habits.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-muted-foreground mb-4">習慣がまだありません</p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="mr-1 h-5 w-5" />
              習慣を追加
            </Button>
          </div>
        )}
      </HabitLayout>

      {/* Form Modal */}
      {showForm && <HabitForm onClose={() => setShowForm(false)} />}

      {/* Log Modal */}
      {logModalTarget && (
        <HabitLogModal
          habitName={logModalTarget.habitName}
          initialData={logModalTarget.existingLog}
          mode={logModalTarget.mode}
          onSave={(data) =>
            saveLog(logModalTarget.habitId, logModalTarget.date, data)
          }
          onSkip={closeLogModal}
          onUncomplete={
            logModalTarget.mode === 'edit'
              ? () => {
                  const { habitId, date } = logModalTarget
                  uncomplete(habitId, date, () => {
                    setHabits((prev) =>
                      prev.map((h) => {
                        if (h.id !== habitId) return h
                        const newDates = (h.completedDates || []).filter(
                          (d) => d !== date
                        )
                        const newRate = calculateRate(h, newDates)
                        return {
                          ...h,
                          completedDates: newDates,
                          achievementRate: newRate,
                        }
                      })
                    )
                  })
                }
              : undefined
          }
        />
      )}
    </>
  )
}
